import { useState } from 'react'
import { Grid, TextField, Divider, Box } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { DateValidationError } from '@mui/x-date-pickers'
import { SwitchContainer } from '../components'
import CheckBox from '../../../shared/UI/CheckBox'
import DropdownMenu from '../../../shared/UI/DropdownMenu'
import { getSelectValuesByType } from '../../../utils/app-utils'
import DateTimePicker from '../../../shared/UI/DateTimePickerV2'
import DatePicker from '../../../shared/UI/DatePickerV2'
import DateRangePicker from '../../../shared/UI/DateRangePickerV2'
import { UkAbsenceFormProps } from './types'
import PartDayAbsenceForm from './partDayAbsenceForm'
import Paragraph from '../../../shared/UI/Paragraph'
import { absenceFormLabels } from '../../../utils/constants'
import AbsenceOverviewCard from '../AbsenceOverviewCard'
import OSPEntitlementCard from '../ManAuthOspEntitlementCard'
import OSPNotificationWithApproval from '../ManAuthPayApprovalBanner'
import NumberInput from '../../../shared/UI/NumberInput'
import { nonSicknessTypesWithInfoBanner } from '../../Absence/types'
import AbsenceDurationWarning from '../AbsenceDurationWarning'
import InfoBanner from '../../../shared/UI/InfoBanner'
import AbsenceLedgerModal from '../AbsenceLedgerModal'

function UKAbsenceForm(props: UkAbsenceFormProps) {
  const [isLedgerModalOpen, setIsLedgerModalOpen] = useState(false)

  const isNonSicknessOrNoShow =
    props.absenceData?.absenceCategoryId === 2 || props.absenceData?.absenceCategoryId === 3

  const isSickness = props.absenceData?.absenceCategoryId === 1

  const isWorkBackDisabled =
    props.managerApprovedHours !== undefined && props.managerApprovedHours > 0

  const handleApprovalHoursChange = (value: number | null) => {
    if (props.onApprovalHoursChange) {
      props.onApprovalHoursChange(value)
    }
  }

  const isWorkBack = props.absenceData?.toWorkBack || props.hasWorkBackDays || false

  const showNonSicknessOspCard =
    props.isManAuthOn &&
    props.absenceData.absenceTypeId !== 0 &&
    nonSicknessTypesWithInfoBanner.includes(props.absenceData.absenceTypeId) &&
    !isWorkBack

  const isValidDateRange = !props.isAbsenceDateRangeError
  const showFutureSicknessAbsenceWarning =
    isSickness && props.hasFutureSicknessAbsence && isValidDateRange
  const showOspEntitlementCard = isSickness && isValidDateRange
  const isValidDateManAuthRequest = props.isManAuthRequest && isValidDateRange

  const futureSicknessAbsenceWarningText = props.extendAbsenceData
    ? absenceFormLabels.futureSicknessAbsenceExtendWarning
    : absenceFormLabels.futureSicknessAbsenceCreateWarning

  return (
    <>
      {props.isAbsenceDurationError && (
        <Grid item xs={12}>
          <AbsenceDurationWarning
            maxDays={props.absenceErrorMaxDays || 1}
            employeeHandbookUrl={props.employeeHandbookUrl}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <DateRangePicker
          value={props.dateRange}
          arrange="Horizontal"
          spacing={2}
          maxRangeDays={2 * 365}
          dataTestId="MyActions-Absence-"
          fromDateError={props.isAbsenceDurationError}
          toDateError={props.isAbsenceDurationError}
          required
          onChange={props.handleDateRangeChange}
          onError={error => {
            if (props.onDateRangeError) {
              const hasValidationError = error !== null && (error[0] !== null || error[1] !== null)
              props.onDateRangeError(hasValidationError)
            }
          }}
        />
      </Grid>
      <Grid container item xs={12} spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Shift"
            name="shiftDescription"
            value={props.absenceData.shiftDescription}
            onChange={props.handleChange}
            disabled={props.isDisableShiftDescription}
            onBlur={props.onShiftStringBlur}
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            label="Expected Return"
            value={props.absenceData.expectedReturn}
            required
            onChange={newValue => {
              if (!newValue) {
                return
              }
              props.setData(absence => ({ ...absence, expectedReturn: new Date(newValue) }))
            }}
            onError={(error: DateValidationError) => {
              const hasError = error !== null
              if (props.onExpectedReturnDateError) {
                props.onExpectedReturnDateError(hasError)
              }
            }}
          />
        </Grid>
      </Grid>
      <PartDayAbsenceForm
        isOpen={props.absenceData.partDayAbsence}
        setIsOpen={props.setOpen}
        popperText={props.popperText}
        shiftDescription={props.absenceData.shiftDescription}
        dateRange={props.dateRange}
        absentFrom={props.absenceData.absentFrom}
        hasHadLunch={props.absenceData.hasHadLunch}
        toWorkBack={props.absenceData.toWorkBack}
        handleDateChange={props.handleDateChange}
        handleDateError={props.handleDateError}
        handleCheckboxChange={props.handleCheckboxChange}
        disabledPartDayAbsence={props.disabledPartDayAbsence}
        disabledAbsentFrom={props.disabledAbsentFrom}
        isWorkBackDisabled={isWorkBackDisabled}
        hasManAuthHistory={props.hasManAuthHistory}
      />
      <Grid item xs={12}>
        <Paragraph weight="bold">{absenceFormLabels.reasonTitleInBold}</Paragraph>
      </Grid>
      <Grid item xs={12} md={12}>
        <DropdownMenu
          label="Absence Type"
          id="absenceType"
          data={props.absenceReasonTypeList}
          textField="displayValue"
          valueField="value"
          name="absenceCategoryId"
          value={props.absenceData.absenceCategoryId}
          onChange={e => {
            const filteredList = props.absenceReasonTypeList.find(
              item => `${item.value}` === `${e.target.value}`
            )
            if (filteredList) {
              props.handleChange(e)
              props.setCurrentTypeAssociatedValueId(filteredList?.value || 0)
            }
          }}
          error={props.validationErrors.absenceTypeId}
          helperText="Required"
          disabled={Boolean(props.extendAbsenceData)}
        />
      </Grid>

      {props.showSplitPotsBalanceError && (
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              borderRadius: '14px',
              padding: '16px',
              backgroundColor: '#FEE4E2',
            }}
          >
            <InfoOutlinedIcon />
            <Box>
              <Paragraph weight="bold" size="14px">Error</Paragraph>
              <Paragraph size="14px">Balances couldn&apos;t be retrieved, please raise a support ticket.</Paragraph>
            </Box>
          </Box>
        </Grid>
      )}

      {showFutureSicknessAbsenceWarning && (
        <Grid item xs={12}>
          <InfoBanner
            dataTestId="future-sickness-warning"
            text={futureSicknessAbsenceWarningText}
          />
        </Grid>
      )}

      {showOspEntitlementCard && (
        <Grid item xs={12} md={6} lg={12}>
          {props.splitPots ? (
            <AbsenceOverviewCard
              data={props.absenceOverviewData ?? null}
              loading={props.isLoadingAbsenceOverview}
              onViewDetail={() => setIsLedgerModalOpen(true)}
              showDiscretionaryPay={(props.absenceOverviewData?.exceededHours ?? 0) > 0 && !isWorkBack}
              onApprovalHoursChange={handleApprovalHoursChange}
              approvalHoursValue={props.approvalHoursValue ?? null}
            />
          ) : (
            <OSPEntitlementCard
              entitlementValue={props.totalSickPayAllowanceHours}
              usedValue={
                typeof props.totalSickPayAllowanceHours === 'number' &&
                typeof props.remainingOSP === 'number'
                  ? props.totalSickPayAllowanceHours - props.remainingOSP
                  : null
              }
              remainingValue={props.totalAvailableOSP}
              loading={props.isLoadingOSP}
              exceededHours={props.exceededHours}
              onApprovalHoursChange={handleApprovalHoursChange}
              approvalHoursValue={props.approvalHoursValue}
              absenceTypeId={props.absenceData.absenceTypeId}
              isManAuthRequest={props.isManAuthRequest}
            />
          )}
        </Grid>
      )}

      {isNonSicknessOrNoShow && (
        <>
          <Grid item xs={12} lg={12}>
            <DropdownMenu
              label="Reason for absence"
              id="absenceReason"
              data={props.absenceTypesList.filter(
                absenceType => absenceType.associatedValue === props.absenceData.absenceCategoryId
              )}
              textField="displayValue"
              valueField="value"
              name="absenceTypeId"
              value={props.absenceData.absenceTypeId}
              onChange={props.handleChange}
              error={props.validationErrors.absenceTypeReasonId}
              helperText="Required"
              disabled={Boolean(props.extendAbsenceData) || !props.currentTypeAssociatedValueId}
            />
          </Grid>

          {showNonSicknessOspCard && (
            <>
              <Grid item xs={12}>
                <OSPNotificationWithApproval
                  exceededHours={props.totalRequestedHours}
                  NonSicknessManAuth
                  absenceTypeId={props.absenceData.absenceTypeId}
                  employeeHandbookUrl={props.employeeHandbookUrl}
                />
              </Grid>
              {isValidDateManAuthRequest && (
                <Grid item xs={12} sx={{ marginTop: '24px' }}>
                  <Box
                    sx={{
                      marginBottom: '24px',
                      width: '100%',
                    }}
                  >
                    <NumberInput
                      label="Hours for approval"
                      id="hours-for-approval"
                      size="small"
                      fullWidth
                      max={props.totalRequestedHours}
                      showMaxValue
                      value={props.approvalHoursValue ?? null}
                      onChange={handleApprovalHoursChange}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          height: '48px',
                          width: '100%',
                        },
                        '& .MuiOutlinedInput-input': {
                          display: 'flex',
                          alignItems: 'center',
                          height: '100%',
                          padding: '0 14px',
                        },
                        '& .MuiInputLabel-outlined': {
                          transform: 'translate(14px, 14px) scale(1)',
                        },
                        '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -6px) scale(0.75)',
                        },
                      }}
                    />
                  </Box>
                </Grid>
              )}
            </>
          )}
        </>
      )}
      <Grid item xs={12} md={12}>
        <Divider sx={{ margin: 0 }} />
      </Grid>
      <Grid item xs={12}>
        <Paragraph weight="bold">{absenceFormLabels.contactInformation}</Paragraph>
      </Grid>
      <Grid item xs={12} md={12}>
        <DropdownMenu
          label="Contact made by"
          id="year-select"
          data={getSelectValuesByType('ContactByType')}
          textField="displayValue"
          valueField="value"
          name="contactedByTypeId"
          value={props.absenceData.contactedByTypeId}
          onChange={props.handleChange}
        />
      </Grid>
      {(props.absenceData.contactedByTypeId === 1 || props.absenceData.contactedByTypeId === 2) && (
        <Grid item xs={12} md={12}>
          <DateTimePicker
            id="absence-contacted-date"
            label="Contacted date"
            required
            value={props.absenceData.contactDateTime}
            onChange={newValue => {
              props.handleDateChange(newValue, 'contactDateTime')
            }}
            onError={(error: string | null) => {
              const hasError = error !== null
              if (props.onContactDateTimeError) {
                props.onContactDateTimeError(hasError)
              }
            }}
            dataTestId="absence-contacted-date"
          />
        </Grid>
      )}
      <Grid item xs={12} md={12}>
        <TextField
          fullWidth
          label="Contact Comments"
          rows={5}
          multiline
          value={props.absenceData.contactComments}
          onChange={props.handleChange}
          name="contactComments"
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <TextField
          fullWidth
          label="Comments"
          rows={5}
          multiline
          value={props.absenceData.comments}
          error={props.validationErrors.comments}
          onChange={props.handleChange}
          name="comments"
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <SwitchContainer>
          <CheckBox
            defaultChecked={props.absenceData.sendFdn}
            label="Send First Day Notification"
            name="sendFdn"
            onChange={props.handleCheckboxChange}
          />
        </SwitchContainer>
      </Grid>
      {props.splitPots && (
        <AbsenceLedgerModal
          open={isLedgerModalOpen}
          onClose={() => setIsLedgerModalOpen(false)}
          overviewRows={props.ledgerOverviewRows}
          breakdownRows={props.ledgerBreakdownRows}
          loading={props.isLoadingLedger}
          hasSplitPots={props.hasSplitPots ?? false}
        />
      )}
    </>
  )
}

export default UKAbsenceForm
